import { useRollbar } from '@rollbar/react';
import { getString } from 'firebase/remote-config';
import { useEffect, useState } from 'react';

import { remoteConfig, remoteConfigReady } from '../firebase.config';

/**
 * Reads Firebase Remote Config values after boot activation, with loading state and error handling.
 * If the configured value cannot be read or parsed, returns the provided default.
 *
 * @param {string} configName - The name of the configuration item to fetch from Firebase Remote Config.
 * @param {FeatureFlags} defaultValue - The default value to be used if the Firebase Remote Config fetch fails.
 * @returns {Object} An object containing the following properties:
 *   - firebaseValue: The value fetched from Firebase Remote Config or the defaultValue if the fetch fails.
 *   - firebaseLoading: A boolean representing the loading state of the Firebase Remote Config fetch.
 */
export const useFirebaseRemoteConfig = <T>(
  configName: string,
  defaultValue: T
) => {
  /**
   * The value fetched from Firebase Remote Config or the defaultValue if the fetch fails.
   * @type {T}
   */
  const [firebaseValue, setFirebaseValue] = useState<T | undefined>(undefined);

  /**
   * A boolean representing the loading state of the Firebase Remote Config fetch.
   * @type {boolean}
   */
  const [firebaseLoading, setFirebaseLoading] = useState<boolean>(true);

  /**
   * An error object representing any errors that occurred during the Firebase Remote Config fetch.
   * @type {unknown | null}
   */
  const [error, setError] = useState<unknown>(null);
  const rollbar = useRollbar();

  useEffect(() => {
    /**
     * Reads the config activated at boot from Firebase's in-memory cache.
     */
    const readConfig = async () => {
      try {
        await remoteConfigReady;
        const value = getString(remoteConfig, configName);
        // No activated value is normal after a failed boot fetch; use the
        // caller's default without creating a second JSON parse failure.
        setFirebaseValue(value ? JSON.parse(value) : undefined);
      } catch (error) {
        rollbar.error('Error fetching remote configs', error as Error);
        setError(error);
      } finally {
        setFirebaseLoading(false);
      }
    };
    readConfig();
  }, [configName, rollbar]);

  /**
   * If there is an error during the Firebase Remote Config fetch, return the default value and loading state.
   */
  if (error) {
    return { firebaseLoading, firebaseValue: defaultValue };
  }

  /**
   * Otherwise, return the fetched value and loading state.
   */
  return {
    firebaseLoading,
    firebaseValue: firebaseValue === undefined ? defaultValue : firebaseValue,
  };
};
